import { useState, useEffect, useMemo } from 'react';
import { database, Episode } from '../lib/database';

export function useEpisodes() {
  const [episodes, setEpisodes] = useState<Episode[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let isMounted = true;
    
    const loadEpisodes = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await database.getEpisodes();
        
        if (isMounted) {
          // Filter to only show published and visible episodes
          const publishedEpisodes = data.filter(episode => 
            episode.status === 'Published' && episode.visible !== false
          );
          setEpisodes(publishedEpisodes);
        }
      } catch (err) {
        console.error('Failed to load episodes:', err);
        if (isMounted) {
          setError('Failed to load episodes');
          // Set empty array as fallback
          setEpisodes([]);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadEpisodes();

    return () => {
      isMounted = false;
    };
  }, []);

  // Get visible episodes (same as episodes but with explicit function)
  const getVisibleEpisodes = useMemo(() => {
    return () => {
      return episodes.filter(episode => 
        episode.status === 'Published' && episode.visible !== false
      );
    };
  }, [episodes]);

  // Get episodes by category
  const getEpisodesByCategory = useMemo(() => {
    return (category: string) => {
      return episodes.filter(episode => 
        episode.category === category || 
        episode.category2 === category || 
        episode.category3 === category || 
        episode.category4 === category || 
        episode.category5 === category
      );
    };
  }, [episodes]);

  // Get latest episodes
  const getLatestEpisodes = useMemo(() => {
    return (limit: number = 3) => {
      return [...episodes]
        .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
        .slice(0, limit);
    };
  }, [episodes]);

  // Get featured episode (with on_focus = true)
  const getFeaturedEpisode = useMemo(() => {
    return () => {
      return episodes.find(episode => episode.on_focus === true) || episodes[0] || null;
    };
  }, [episodes]);

  // Search episodes
  const searchEpisodes = useMemo(() => {
    return (query: string) => {
      if (!query.trim()) return episodes;
      
      const searchTerm = query.toLowerCase();
      return episodes.filter(episode =>
        episode.title.toLowerCase().includes(searchTerm) ||
        episode.description.toLowerCase().includes(searchTerm) ||
        episode.guest.toLowerCase().includes(searchTerm) ||
        episode.category?.toLowerCase().includes(searchTerm)
      );
    };
  }, [episodes]);

  // Increment episode views
  const incrementViews = async (episodeId: number) => {
    try {
      await database.incrementEpisodeViews(episodeId);
      // Update local state to reflect the increment
      setEpisodes(prevEpisodes => 
        prevEpisodes.map(episode => 
          episode.id === episodeId 
            ? { ...episode, viewCount: (episode.viewCount || 0) + 1 }
            : episode
        )
      );
    } catch (error) {
      console.error('Failed to increment views:', error);
    }
  };

  return {
    episodes,
    loading,
    error,
    getVisibleEpisodes,
    getEpisodesByCategory,
    getLatestEpisodes,
    getFeaturedEpisode,
    searchEpisodes,
    incrementViews,
    refreshEpisodes: () => {
      setLoading(true);
      database.getEpisodes().then(data => {
        const publishedEpisodes = data.filter(episode => 
          episode.status === 'Published' && episode.visible !== false
        );
        setEpisodes(publishedEpisodes);
        setLoading(false);
      }).catch(err => {
        console.error('Failed to refresh episodes:', err);
        setError('Failed to refresh episodes');
        setLoading(false);
      });
    },
    // Admin functions for episode management
    createEpisode: async (episodeData: Omit<Episode, 'id' | 'viewCount' | 'createdAt' | 'updatedAt'>) => {
      try {
        const newEpisode = await database.createEpisode(episodeData);
        // Refresh episodes after creation
        const data = await database.getEpisodes();
        setEpisodes(data);
        return newEpisode;
      } catch (error) {
        console.error('Failed to create episode:', error);
        throw error;
      }
    },
    updateEpisode: async (id: number, updates: Partial<Episode>) => {
      try {
        const updatedEpisode = await database.updateEpisode(id, updates);
        // Refresh episodes after update
        const data = await database.getEpisodes();
        setEpisodes(data);
        return updatedEpisode;
      } catch (error) {
        console.error('Failed to update episode:', error);
        throw error;
      }
    }
  };
}